/**
 * Turning whatever was thrown into something a surface can render.
 *
 * Three shapes reach this module:
 *
 *  1. An `ORPCError` whose `data` is the server's `AppError` — the normal case.
 *     `@crm/api-client`'s `customErrorResponseBodyDecoder` guarantees it for
 *     every problem+json response.
 *  2. A `TypeError` from `fetch` — the network never reached the server. There is
 *     no correlation id, and pretending otherwise would send the user chasing a
 *     log line that does not exist.
 *  3. Anything else (a bug in our own code).
 *
 * `unsupportedFilter` exists because the API answers a contract filter it cannot
 * honour with a 422 naming that filter (`contacts.list.q`) rather than silently
 * ignoring it. That is a *client* bug when it happens — we sent a control the
 * server does not implement — and it has to read as one, not as "your search was
 * invalid".
 */

import { type AppError, isAppError } from '@crm/contracts';

export type ClientError = {
  readonly code: AppError['code'] | 'network' | 'unknown';
  readonly message: string;
  readonly retryable: boolean;
  /** Present only when the server answered; `null` for a network failure. */
  readonly correlationId: string | null;
  /** `contacts.list.q` for a 422 naming an unimplemented filter. */
  readonly unsupportedFilter: string | null;
  readonly details?: unknown;
};

/** `not implemented: contacts.list.q` → `contacts.list.q`. */
const UNSUPPORTED_FILTER_RE = /not implemented:\s*([a-z][\w.]*(?:\.[\w=]+)*)/i;

function dataOf(error: unknown): unknown {
  return typeof error === 'object' && error !== null && 'data' in error
    ? (error as { data?: unknown }).data
    : undefined;
}

function messageOf(error: unknown): string {
  if (error instanceof Error && error.message !== '') {
    return error.message;
  }
  return typeof error === 'string' && error !== '' ? error : 'Something went wrong.';
}

/**
 * A `fetch` rejection. Narrow on purpose: an `ORPCError` is also an `Error`, so
 * this must run only after the `AppError` check.
 */
function isNetworkFailure(error: unknown): boolean {
  return error instanceof TypeError || (error instanceof Error && error.name === 'AbortError');
}

export function toClientError(error: unknown): ClientError {
  const data = dataOf(error);
  if (isAppError(data)) {
    const match = UNSUPPORTED_FILTER_RE.exec(data.message);
    return {
      code: data.code,
      message: data.message,
      retryable: data.retryable,
      correlationId: data.correlationId === '' ? null : data.correlationId,
      unsupportedFilter: match?.[1] ?? null,
      ...(data.details === undefined ? {} : { details: data.details }),
    };
  }

  if (isNetworkFailure(error)) {
    return {
      code: 'network',
      message: 'Could not reach the server.',
      // The one failure class where a blind retry is always safe: the request
      // may not have been received at all, and every mutation carries an
      // Idempotency-Key so a duplicate is collapsed server-side.
      retryable: true,
      correlationId: null,
      unsupportedFilter: null,
    };
  }

  return {
    code: 'unknown',
    message: messageOf(error),
    retryable: false,
    correlationId: null,
    unsupportedFilter: null,
  };
}

/** True when the failure is the API telling us we sent a filter it cannot honour. */
export function isUnsupportedFilterError(error: ClientError): boolean {
  return error.code === 'validation_failed' && error.unsupportedFilter !== null;
}

/**
 * Maps a server 422's `details` to per-field messages, when it carries them.
 *
 * The API's zod failures put issues on `details`; the shape is not pinned by the
 * contract, so this reads defensively and returns `{}` rather than guessing. A
 * field message we cannot place still reaches the user through the toast.
 */
export function fieldErrorsOf(error: ClientError): Record<string, string> {
  const details = error.details;
  if (typeof details !== 'object' || details === null) {
    return {};
  }
  const issues = (details as { issues?: unknown }).issues;
  if (!Array.isArray(issues)) {
    return {};
  }
  const out: Record<string, string> = {};
  for (const issue of issues) {
    if (typeof issue !== 'object' || issue === null) continue;
    const { path, message } = issue as { path?: unknown; message?: unknown };
    if (!Array.isArray(path) || typeof message !== 'string') continue;
    const key = path
      .filter((part) => typeof part === 'string' || typeof part === 'number')
      .join('.');
    if (key !== '' && out[key] === undefined) {
      out[key] = message;
    }
  }
  return out;
}
